import React, { useState, useEffect } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  Alert,
  Snackbar,
  Typography,
  Button,
  IconButton,
  Container,
  Paper,
  Fade,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Menu as MenuIcon,
  Chat as ChatIcon,
  Info as InfoIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageService, Conversation } from '../services/messageService';
import { OffersService } from '../services/offersService';
import { testOffersTable, testUserAuth } from '../services/testOffersService';
import ChatWindow from '../components/ChatWindow';
import OfferPopup from '../components/ChatWindow/OfferPopup';
import MessagesSidebar from '../components/MessagesSidebar/MessagesSidebar';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { supabase } from '../lib/supabase';

const isUuid = (value: string | undefined | null): boolean => {
  const v = String(value || '').trim();
  if (!v) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
};

const MessagesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { messagesSidebarOpen, setMessagesSidebarOpen, pendingConversationTarget, setPendingConversationTarget } = useUIStore();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | undefined>();
  const [chatHeaderOpen, setChatHeaderOpen] = useState(false);
  
  // Use ref to track selected conversation ID to prevent unwanted changes
  const selectedConversationIdRef = React.useRef<string | undefined>(undefined);

  // Prevent duplicate pending-target processing (React strict mode / rerenders)
  const lastPendingKeyRef = React.useRef<string | null>(null);

  // Debug selected conversation changes
  useEffect(() => {
    console.log('MessagesPage - selectedConversation changed to:', {
      id: selectedConversation?.id,
      productId: selectedConversation?.product_id,
      subject: selectedConversation?.subject,
      otherUser: selectedConversation?.other_user?.firstName,
      lastMessage: selectedConversation?.last_message?.content
    });
    
    // Update ref when selected conversation changes
    selectedConversationIdRef.current = selectedConversation?.id;
    setChatHeaderOpen(false);
  }, [selectedConversation]);

  // Ensure selectedConversation stays in sync with conversations list
  useEffect(() => {
    if (selectedConversationIdRef.current && conversations.length > 0) {
      const updatedConversation = conversations.find(conv => conv.id === selectedConversationIdRef.current);
      if (updatedConversation) {
        // Always update to ensure we have the latest conversation object
        console.log('MessagesPage - Syncing selectedConversation with updated conversations list');
        setSelectedConversation(updatedConversation);
      }
    }
  }, [conversations]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [previousPage, setPreviousPage] = useState<{ path: string; title: string } | null>(null);
  
  // Offer popup state
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [offerData, setOfferData] = useState<{
    productTitle: string;
    productPrice: number;
    sellerName: string;
    conversationId?: string;
  } | null>(null);
  const [offerLoading, setOfferLoading] = useState(false);

  // Conversations are loaded on demand (e.g. when the sidebar opens)

  // Listen for new messages to update conversation list in real-time
  useEffect(() => {
    if (!user?.id) return;

    // Listen to local message events (when user sends a message)
    const unsubscribeLocal = MessageService.onLocalMessage((message) => {
      console.log('MessagesPage - Received local message:', message);
      updateConversationWithMessage(message);
    });

    // Listen for incoming messages from other users
    const subscriptions: any[] = [];
    
    // Subscribe to each specific conversation for real-time updates
    conversations.forEach(conv => {
      if (conv.id) {
        const subscription = MessageService.subscribeToConversationMessages(
          conv.id,
          (message) => {
            console.log('MessagesPage - Received incoming message for conversation:', conv.id, message);
            updateConversationWithMessage(message);
          }
        );
        subscriptions.push(subscription);
      }
    });

    return () => {
      unsubscribeLocal();
      subscriptions.forEach(sub => {
        try {
          sub?.unsubscribe?.();
        } catch (e) {
          console.warn('Error unsubscribing from message subscription:', e);
        }
      });
    };
  }, [user?.id, conversations.length, selectedConversation?.id]);

  // Helper function to update conversation with new message
  const updateConversationWithMessage = (message: any) => {
    const currentSelectedId = selectedConversationIdRef.current;
    console.log('MessagesPage - updateConversationWithMessage called with:', {
      messageId: message.id,
      conversationId: message.conversation_id,
      content: message.content,
      currentSelectedConversation: currentSelectedId,
      isSelectedConversation: currentSelectedId === message.conversation_id
    });
    
    // Update the selected conversation FIRST if it's the one that received the message
    // This ensures the selected conversation object is updated with the new message
    if (currentSelectedId === message.conversation_id) {
      console.log('MessagesPage - Updating selected conversation with new message');
      setSelectedConversation(prevSelected => {
        if (!prevSelected) return prevSelected;
        
        return {
          ...prevSelected,
          last_message: {
            id: message.id,
            sender_id: message.sender_id,
            receiver_id: message.receiver_id,
            conversation_id: message.conversation_id,
            content: message.content,
            message_type: message.message_type,
            is_read: message.is_read,
            created_at: message.created_at,
            updated_at: message.updated_at,
            sender: message.sender,
            receiver: message.receiver
          },
          last_message_at: message.created_at,
          updated_at: message.created_at
        };
      });
    }
    
    setConversations(prevConversations => {
      const updatedConversations = prevConversations.map(conv => {
        if (conv.id === message.conversation_id) {
          console.log('MessagesPage - Updating conversation in list:', conv.id, 'with message:', message.content);
          // Update the conversation with the new last message
          return {
            ...conv,
            last_message: {
              id: message.id,
              sender_id: message.sender_id,
              receiver_id: message.receiver_id,
              conversation_id: message.conversation_id,
              content: message.content,
              message_type: message.message_type,
              is_read: message.is_read,
              created_at: message.created_at,
              updated_at: message.updated_at,
              sender: message.sender,
              receiver: message.receiver
            },
            last_message_at: message.created_at,
            updated_at: message.created_at
          };
        }
        return conv;
      });
      
      // Log if no conversation was found
      const foundConversation = updatedConversations.find(conv => conv.id === message.conversation_id);
      if (!foundConversation) {
        console.warn('MessagesPage - No conversation found for message:', message.conversation_id);
      }
      
      // Sort conversations by last_message_at to keep most recent first
      const sortedConversations = updatedConversations.sort((a, b) => 
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );
      
      console.log('MessagesPage - Conversations reordered. Selected conversation ID:', currentSelectedId);
      
      return sortedConversations;
    });
  };

  // Track previous page from location state
  useEffect(() => {
    if (location.state?.from) {
      setPreviousPage(location.state.from);
    } else {
      // Default fallback pages based on user role or common paths
      const referrer = document.referrer;
      if (referrer.includes('/product/')) {
        setPreviousPage({ path: '/all-products', title: 'Products' });
      } else if (referrer.includes('/dashboard')) {
        setPreviousPage({ path: '/dashboard', title: 'Dashboard' });
      } else {
        setPreviousPage({ path: '/all-products', title: 'Products' });
      }
    }
  }, [location.state]);

  // Handle back navigation
  const handleBackNavigation = () => {
    if (previousPage) {
      navigate(previousPage.path);
    } else {
      // Fallback to dashboard or home
      navigate('/dashboard');
    }
  };

  // Get appropriate icon for the back button
  const getBackIcon = () => {
    if (!previousPage) return <HomeIcon />;
    
    switch (previousPage.path) {
      case '/dashboard':
        return <DashboardIcon />;
      case '/all-products':
      case '/products':
        return <StoreIcon />;
      default:
        return <ArrowBackIcon />;
    }
  };

  // Handle conversation from URL query parameter
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    // If we have a pendingConversationTarget (coming from product page), it should be authoritative
    // ONLY while we don't have a real UUID conversation selected via URL.
    if (pendingConversationTarget && !(conversationId && isUuid(conversationId))) return;

    if (conversationId && user?.id && isUuid(conversationId)) {
      const loadConversationFromUrl = async () => {
        try {
          console.log('MessagesPage - Loading conversation from URL:', conversationId);
          try {
            setPendingConversationTarget(undefined);
          } catch {}
          let conversation = await MessageService.getConversationById(conversationId);
          if (conversation) {
            let conv: Conversation = conversation;
            // Ensure product details are present (needed for offer cards)
            try {
              if (!conv.product && conv.product_id) {
                const { data: prod } = await supabase
                  .from('products')
                  .select('id, name, price, images')
                  .eq('id', conv.product_id)
                  .single();
                if (prod) {
                  conv = {
                    ...conv,
                    product: {
                      id: prod.id,
                      name: prod.name,
                      price: prod.price,
                      images: Array.isArray(prod.images) ? prod.images : [],
                    }
                  } as any;
                }
              }
            } catch (e) {
              console.warn('MessagesPage - Failed to enrich conversation with product', e);
            }
            setSelectedConversation(conv);
            console.log('MessagesPage - Loaded conversation:', conv);
          } else {
            console.error('MessagesPage - Conversation not found:', conversationId);
            setError('Conversation not found');
          }
        } catch (error) {
          console.error('MessagesPage - Error loading conversation:', error);
          setError('Failed to load conversation');
        }
      };
      
      loadConversationFromUrl();
    }
  }, [searchParams, user?.id, pendingConversationTarget]);

  // Handle navigation from product detail page
  useEffect(() => {
    if (location.state?.recipientId) {
      const { recipientId, recipientName, productId, productTitle, isOffer, productPrice } = location.state;
      
      // Check if conversation already exists
      const existingConversation = conversations.find(
        conv => conv.other_user?.id === recipientId && conv.product_id === productId
      );
      
      if (existingConversation) {
        setSelectedConversation(existingConversation);
        
        // If this is an offer and we found an existing conversation, show the offer popup
        if (isOffer && productPrice) {
          setOfferData({
            productTitle,
            productPrice,
            sellerName: recipientName,
            conversationId: existingConversation.id,
          });
          setShowOfferPopup(true);
        }
      } else {
        // Create conversation with product details
        const createConversationWithProduct = async () => {
          try {
            const subject = isOffer ? `Offer for ${productTitle}` : `Inquiry about ${productTitle}`;
            const newConversation = await MessageService.createConversationWithProduct(
              recipientId,
              productId,
              subject
            );
            
            setSelectedConversation(newConversation);
            
            // Add the new conversation to the list immediately
            setConversations(prevConversations => {
              // Check if conversation already exists to avoid duplicates
              const exists = prevConversations.some(conv => conv.id === newConversation.id);
              if (exists) {
                return prevConversations;
              }
              return [newConversation, ...prevConversations];
            });
            
            // If this is an offer, show the offer popup
            if (isOffer && productPrice) {
              // Test database and auth before showing offer popup
              console.log('Testing database and auth before offer popup...');
              const tableTest = await testOffersTable();
              const authTest = await testUserAuth();
              
              console.log('Table test result:', tableTest);
              console.log('Auth test result:', authTest);
              
              setOfferData({
                productTitle,
                productPrice,
                sellerName: recipientName,
                conversationId: newConversation.id,
              });
              setShowOfferPopup(true);

              // Clear isOffer flag from history state to prevent popup reopening on reload
              try {
                navigate(location.pathname, { 
                  replace: true, 
                  state: { 
                    ...location.state, 
                    isOffer: false 
                  } 
                });
              } catch {}
            }
          } catch (error) {
            console.error('Error creating conversation with product:', error);
            // Fallback to creating a temporary conversation
            const tempConversation: Conversation = {
              id: `temp-${Date.now()}`,
              user1_id: user?.id || '',
              user2_id: recipientId,
              product_id: productId,
              subject: `Inquiry about ${productTitle}`,
              last_message_at: new Date().toISOString(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              other_user: {
                id: recipientId,
                firstName: recipientName.split(' ')[0] || 'Vendor',
                lastName: recipientName.split(' ').slice(1).join(' ') || '',
                avatar: undefined,
              },
              unread_count: 0,
            };
            
            setSelectedConversation(tempConversation);
          }
        };
        
        createConversationWithProduct();
      }
    } else if (showOfferPopup) {
      // Defensive: if popup is somehow open without state, close it on mount
      setShowOfferPopup(false);
      setOfferData(null);
    }
  }, [location.state, conversations, user?.id, isMobile]);

  // Handle pendingConversationTarget from UI store (e.g., from Make Offer button)
  useEffect(() => {
    if (pendingConversationTarget && user?.id) {
      const { receiverId, productId, subject } = pendingConversationTarget;

      const pendingKey = `${receiverId || ''}::${productId || ''}`;
      if (pendingKey && lastPendingKeyRef.current === pendingKey) {
        return;
      }
      lastPendingKeyRef.current = pendingKey;
      
      console.log('MessagesPage - Handling pendingConversationTarget:', {
        receiverId,
        productId,
        subject
      });

      // Optimistic: show chat window immediately so user can start typing
      const tempConversation: Conversation = {
        id: `pending-${Date.now()}`,
        user1_id: user.id,
        user2_id: receiverId,
        product_id: productId,
        subject: subject || 'Inquiry',
        last_message_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        other_user: {
          id: receiverId,
          firstName: 'User',
          lastName: '',
          avatar: undefined,
          alias: undefined,
        },
        unread_count: 0,
      };

      setSelectedConversation(tempConversation);
      setMessagesSidebarOpen(false);
      
      const handlePendingTarget = async () => {
        try {
          // Always resolve via backend to avoid selecting a stale/wrong local conversation
          console.log('MessagesPage - Resolving product inquiry conversation for pending target');
          const resolvedConversation = await MessageService.createConversationWithProduct(
            receiverId,
            productId,
            subject || `Inquiry about product`
          );
          
          console.log('MessagesPage - Resolved conversation:', resolvedConversation.id);
          setSelectedConversation(resolvedConversation);

          try {
            navigate(`/messages?conversation=${resolvedConversation.id}`, { replace: true });
          } catch {}
          
          // Add the conversation to the list immediately
          setConversations(prevConversations => {
            const exists = prevConversations.some(conv => conv.id === resolvedConversation.id);
            if (exists) {
              return prevConversations;
            }
            return [resolvedConversation, ...prevConversations];
          });
          
          // Close the sidebar to show the chat window
          setMessagesSidebarOpen(false);
          // Clear the pending target
          setPendingConversationTarget(undefined);
          
        } catch (error) {
          console.error('MessagesPage - Error handling pendingConversationTarget:', error);
          // Clear the pending target even on error to prevent infinite retries
          setPendingConversationTarget(undefined);
        }
      };
      
      handlePendingTarget();
    }
  }, [pendingConversationTarget, user?.id, navigate, setMessagesSidebarOpen, setPendingConversationTarget]);

  // Load conversations when messages sidebar opens
  useEffect(() => {
    if (messagesSidebarOpen && conversations.length === 0 && user?.id) {
      console.log('MessagesPage - Loading conversations for sidebar');
      loadConversations();
    }
  }, [messagesSidebarOpen, conversations.length, user?.id]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(undefined);
      
      const conversationsData = await MessageService.getConversations();
      setConversations(conversationsData);
    } catch (err: any) {
      console.error('Error loading conversations:', err);
      const specific = err?.message || err?.error || 'Unknown error';
      const details = err?.details ? ` Details: ${err.details}` : '';
      const hint = err?.hint ? ` Hint: ${err.hint}` : '';
      const code = err?.code ? ` (code: ${err.code})` : '';
      setError(`Failed to load conversations: ${specific}${code}${details}${hint}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSelect = async (conversation: Conversation) => {
    console.log('MessagesPage - handleConversationSelect called with:', {
      conversationId: conversation.id,
      productId: conversation.product_id,
      subject: conversation.subject,
      otherUserId: conversation.other_user?.id,
      previousSelectedId: selectedConversation?.id
    });
    
    // Remove current user from previous conversation's active users
    if (selectedConversation && user) {
      try {
        await MessageService.removeUserFromConversation(selectedConversation.id, user.id);
        console.log('MessagesPage - Removed user from previous conversation:', selectedConversation.id);
      } catch (error) {
        console.error('MessagesPage - Error removing user from previous conversation:', error);
      }
    }
    
    // Add current user to new conversation's active users
    if (user) {
      try {
        await MessageService.addUserToConversation(conversation.id, user.id);
        console.log('MessagesPage - Added user to new conversation:', conversation.id);
      } catch (error) {
        console.error('MessagesPage - Error adding user to new conversation:', error);
      }
    }
    
    // Only allow conversation selection through explicit user action
    setSelectedConversation(conversation);
    
    console.log('MessagesPage - Setting selected conversation to:', conversation.id);
  };

  // Handle offer submission
  const handleOfferSubmit = async (offerData: { price: number; message: string }) => {
    console.log('MessagesPage.handleOfferSubmit - Starting with:', {
      offerData,
      selectedConversationId: selectedConversation?.id,
      locationState: location.state
    });

    if (!selectedConversation?.id || !location.state?.productId || !location.state?.recipientId) {
      console.error('MessagesPage.handleOfferSubmit - Missing required data:', {
        selectedConversationId: selectedConversation?.id,
        productId: location.state?.productId,
        recipientId: location.state?.recipientId
      });
      setError('Missing required information. Please try again.');
      return;
    }
    
    setOfferLoading(true);
    try {
      // 1) Create the offer (own try/catch for precise errors)
      const createOfferData = {
        product_id: location.state.productId,
        seller_id: location.state.recipientId,
        offer_price: offerData.price,
        offer_message: offerData.message,
      };

      console.log('MessagesPage.handleOfferSubmit - Creating offer with data:', createOfferData);

      let createdOffer: any = null;
      try {
        createdOffer = await OffersService.createOffer(createOfferData);
      } catch (error: any) {
        console.error('Offer creation failed:', error);
        // Map offer creation errors precisely
        let errorMessage = 'Failed to create offer. Please try again.';
        if (error?.message) {
          if (error.message.includes('duplicate key')) {
            errorMessage = 'You have already made an offer for this product.';
          } else if (error.message.includes('permission denied')) {
            errorMessage = 'You do not have permission to create offers. Please sign in.';
          } else if (error.message.includes('violates check constraint')) {
            if (error.message.includes('offer_price')) {
              errorMessage = 'Offer price must be greater than 0.';
            } else if (error.message.includes('offer_message')) {
              errorMessage = 'Offer message must be at least 10 characters long.';
            } else if (error.message.includes('different_buyer_seller')) {
              errorMessage = 'You cannot make an offer on your own product.';
            }
          } else if (error.message.includes('foreign key')) {
            errorMessage = 'Invalid product or user. Please refresh and try again.';
          } else {
            errorMessage = `Error: ${error.message}`;
          }
        }
        setError(errorMessage);
        return; // Stop here if the offer itself failed
      }

      // 2) Send a notification message (separate try/catch)
      try {
        // Send rich offer message to the receiver with linkage to product and offer
        await MessageService.sendOfferMessage(
          selectedConversation.other_user?.id || '',
          location.state.productId,
          createdOffer?.id,
          offerData.price,
          offerData.message,
          location.state.subject || undefined,
          selectedConversation.id // Pass the current conversation ID to ensure message goes to the right conversation
        );
      } catch (msgErr: any) {
        console.error('Offer created, but sending notification message failed (detailed):', msgErr);
        setShowOfferPopup(false);
        setOfferData(null);
        const specific = msgErr?.message || msgErr?.error || 'Unknown error';
        const details = msgErr?.details ? ` Details: ${msgErr.details}` : '';
        const hint = msgErr?.hint ? ` Hint: ${msgErr.hint}` : '';
        const code = msgErr?.code ? ` (code: ${msgErr.code})` : '';
        setError(`Offer created, but sending the notification message failed: ${specific}${code}${details}${hint}`);
        return;
      }

      // Success flow
      setShowOfferPopup(false);
      setOfferData(null);
      setError(undefined);

    } catch (error: any) {
      console.error('Error creating offer:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to create offer. Please try again.';
      
      if (error?.message) {
        if (error.message.includes('duplicate key')) {
          errorMessage = 'You have already made an offer for this product.';
        } else if (error.message.includes('permission denied')) {
          errorMessage = 'You do not have permission to create offers. Please sign in.';
        } else if (error.message.includes('violates check constraint')) {
          if (error.message.includes('offer_price')) {
            errorMessage = 'Offer price must be greater than 0.';
          } else if (error.message.includes('offer_message')) {
            errorMessage = 'Offer message must be at least 10 characters long.';
          } else if (error.message.includes('different_buyer_seller')) {
            errorMessage = 'You cannot make an offer on your own product.';
          }
        } else if (error.message.includes('foreign key')) {
          errorMessage = 'Invalid product or user. Please refresh and try again.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setOfferLoading(false);
    }
  };

  const handleBackClick = () => {
    if (previousPage) {
      navigate(previousPage.path);
    } else {
      // Fallback to products page
      navigate('/all-products');
    }
  };


  // Subscribe to real-time updates
  useEffect(() => {
    if (!user) return;

    let conversationSubscription: any = null;
    let activeUsersSubscription: any = null;

    const setupSubscription = async () => {
      conversationSubscription = await MessageService.subscribeToConversations((conversation) => {
        console.log('MessagesPage - Received conversation update from subscription:', {
          conversationId: conversation.id,
          currentSelectedConversation: selectedConversation?.id,
          willUpdateSelected: conversation.id === selectedConversation?.id
        });
        
        setConversations(prev => {
          const existingIndex = prev.findIndex(conv => conv.id === conversation.id);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = conversation;
            // Sort conversations by last_message_at to keep most recent first
            return updated.sort((a, b) => 
              new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
            );
          } else {
            return [conversation, ...prev];
          }
        });
        
        // CRITICAL: Only update the selected conversation if it's the one being updated
        // Use ref to ensure we don't accidentally switch conversations
        if (selectedConversationIdRef.current === conversation.id) {
          console.log('MessagesPage - Updating selected conversation from subscription');
          setSelectedConversation(conversation);
        }
      });

      // Subscribe to active users changes
      activeUsersSubscription = supabase
        .channel('conversations_active_users')
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `user1_id=eq.${user.id} OR user2_id=eq.${user.id}`
        }, (payload) => {
          console.log('MessagesPage - Active users updated:', payload);
          const updatedConversation = payload.new as any;
          
          setConversations(prev => {
            const existingIndex = prev.findIndex(conv => conv.id === updatedConversation.id);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                active_users: updatedConversation.active_users
              };
              return updated;
            }
            return prev;
          });
        })
        .subscribe();
    };

    setupSubscription();

    return () => {
      conversationSubscription?.unsubscribe();
      activeUsersSubscription?.unsubscribe();
    };
  }, [user, selectedConversation?.id]);

  // Cleanup: Remove user from active conversation when component unmounts or user changes
  useEffect(() => {
    return () => {
      // Remove user from current conversation when component unmounts
      if (selectedConversation && user) {
        MessageService.removeUserFromConversation(selectedConversation.id, user.id)
          .then(() => {
            console.log('MessagesPage - Cleanup: Removed user from conversation on unmount');
          })
          .catch((error) => {
            console.error('MessagesPage - Cleanup: Error removing user from conversation:', error);
          });
      }
    };
  }, [selectedConversation?.id, user?.id]);

  if (!user) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'grey.50',
      }}>
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          Please log in to access messages.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ 
        height: '100vh',
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        bgcolor: 'grey.50',
        position: 'relative',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
      }}>

        {selectedConversation?.product_id && (
          <Box
            sx={{
              position: 'absolute',
              top: chatHeaderOpen ? 108 : 16,
              left: 16,
              zIndex: 25,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              transition: 'top 0.18s ease',
            }}
          >
            <Tooltip title="Back to product" placement="right">
              <IconButton
                onClick={() => navigate(`/product/${selectedConversation.product_id}`, { state: { fromMessages: true } })}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  border: `1px solid rgba(0,0,0,0.08)`,
                  boxShadow: theme.shadows[6],
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: 'primary.main',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[10],
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={chatHeaderOpen ? 'Hide info' : 'Show info'} placement="right">
              <IconButton
                onClick={() => setChatHeaderOpen(v => !v)}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  border: `1px solid rgba(0,0,0,0.08)`,
                  boxShadow: theme.shadows[6],
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  color: 'primary.main',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[10],
                  },
                }}
              >
                {chatHeaderOpen ? <ExpandLessIcon /> : <InfoIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Messages Sidebar */}
        <MessagesSidebar
          open={messagesSidebarOpen}
          onClose={() => setMessagesSidebarOpen(false)}
        />

        {/* Chat Window */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          {selectedConversation ? (
            <ChatWindow
              conversation={selectedConversation}
              onBack={undefined}
              headerOpen={chatHeaderOpen}
              onHeaderOpenChange={setChatHeaderOpen}
            />
          ) : (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 4,
              px: 3,
            }}>
              {/* Beautiful Empty State */}
              <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                <Box sx={{ 
                  width: 120, 
                  height: 120, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}>
                  <ChatIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                </Box>
                
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                  Welcome to Messages
                </Typography>
                
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  {conversations.length > 0 
                    ? 'Select a conversation to start chatting'
                    : 'Start a conversation from a product page or user profile'
                  }
                </Typography>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<ChatIcon />}
                    onClick={() => setMessagesSidebarOpen(true)}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {conversations.length > 0 ? 'View Conversations' : 'Browse Products'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={getBackIcon()}
                    onClick={handleBackNavigation}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {previousPage ? `Back to ${previousPage.title}` : 'Back to Dashboard'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(undefined)}
      >
        <Alert 
          severity="error" 
          onClose={() => setError(undefined)}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Offer Popup */}
      {offerData && (
        <OfferPopup
          open={showOfferPopup}
          onClose={() => {
            setShowOfferPopup(false);
            setOfferData(null);
          }}
          onSubmit={handleOfferSubmit}
          productTitle={offerData.productTitle}
          productPrice={offerData.productPrice}
          sellerName={offerData.sellerName}
          loading={offerLoading}
        />
      )}
    </>
  );
};

export default MessagesPage;
